import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { usePeopleSearch } from "@/contexts/PeopleSearchContext";
import { useSkillFacets } from "@/hooks/peopleSearch/useSkillFacets";

export function SkillsPanel() {
	const { t } = useTranslation();
	const { draft, updateDraft } = usePeopleSearch();
	const { data: facets, isLoading } = useSkillFacets();
	const [filterText, setFilterText] = useState('');

	const visibleFacets = useMemo(() => {
		const query = filterText.trim().toLowerCase();
		const all = facets || [];
		if (query === '')
			return all.slice(0, 100);
		return all.filter(f => f.name.toLowerCase().includes(query)).slice(0, 100);
	}, [facets, filterText]);

	const toggleSkill = (name: string, checked: boolean) => {
		const names = checked
			? [...draft.skills.names, name]
			: draft.skills.names.filter(n => n !== name);
		updateDraft('skills', { ...draft.skills, names });
	};

	return (
		<div className="flex h-full min-h-0 flex-col gap-4">
			<div className="flex items-center justify-between gap-2 shrink-0">
				<p className="text-sm text-muted-foreground">
					{t("peopleSearch.skills.hint", "Candidates with these skills")}
				</p>
				<ToggleGroup
					type="single"
					value={draft.skills.mode}
					onValueChange={v => { if (v) updateDraft('skills', { ...draft.skills, mode: v as 'any' | 'all' }); }}
					variant="outline"
					size="sm"
				>
					<ToggleGroupItem value="any">{t("peopleSearch.skills.any", "Any")}</ToggleGroupItem>
					<ToggleGroupItem value="all">{t("peopleSearch.skills.all", "All")}</ToggleGroupItem>
				</ToggleGroup>
			</div>
			<div className="relative shrink-0">
				<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
				<Input
					value={filterText}
					onChange={e => setFilterText(e.target.value)}
					placeholder={t("peopleSearch.skills.searchPlaceholder", "Search skills…")}
					className="pl-8"
				/>
			</div>
			{isLoading ? (
				<div className="space-y-2">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-6 w-full" />
					))}
				</div>
			) : (
				<ScrollArea className="flex-1 min-h-[160px] border rounded-sm p-2">
					<div className="space-y-1">
						{visibleFacets.map(facet => (
							<label key={facet.name} className="flex items-center gap-2 px-1 py-1 rounded-sm hover:bg-muted cursor-pointer">
								<Checkbox
									checked={draft.skills.names.includes(facet.name)}
									onCheckedChange={checked => toggleSkill(facet.name, !!checked)}
								/>
								<span className="text-sm flex-1 truncate">{facet.name}</span>
								{facet.count !== null && (
									<span className="text-xs text-muted-foreground">{facet.count}</span>
								)}
							</label>
						))}
						{visibleFacets.length === 0 && (
							<p className="text-sm text-muted-foreground px-1 py-2">
								{t("peopleSearch.skills.noResults", "No skills found")}
							</p>
						)}
					</div>
				</ScrollArea>
			)}
		</div>
	);
}
